import Header from '@/components/elements/Header/Header';
import Select from '@/components/elements/forms/Select/Select';
import Button from '@/components/elements/forms/buttons/Button/Button';
import ListInput from '@/components/elements/forms/listInput/ListInput/ListInput';
import ListInputSelect from '@/components/elements/forms/listInput/ListInputSelect/ListInputSelect';
import ListInputTextArea from '@/components/elements/forms/listInput/ListInputTextArea/ListInputTextArea';
import Alert from '@/components/elements/notifications/Alert/Alert';
import PillTabBar from '@/components/elements/tabs/PillTabBar/PillTabBar';
import Icon from '@/components/icons/Icon';
import CardWrapper from '@/components/modules/CardWrapper';
import { capitalizeFirstLetter, classnames, isEmpty, isZipcode } from '@/helpers';
import { guestCheckoutManager, isStandardFormField } from '@/helpers/checkout';
import { getContainmentMode } from '@/helpers/containmentPolicy';
import { isNationalId } from '@/helpers/utilsTS';
import { useAppDispatch } from '@/hooks';
import { LoginContext } from '@/hooks/useLogin';
import { useWidgetLogin } from '@/hooks/useWidgetLogin';
import { _s, _t } from '@/locale';
import { handleLoginClick } from '@/services/navigationServices';
import { FormField } from '@/types/state/shared';
import { Fragment, useContext, useEffect, useState } from 'react';
import { UseFormRegister } from 'react-hook-form';
import isEmail from 'validator/lib/isEmail';
import isLength from 'validator/lib/isLength';
import isMobilePhone from 'validator/lib/isMobilePhone';
import { DEFAULT_FORM_FIELD_ID, GUEST_FORM_FIELD_ID, PROFILE_FORM_FIELD_ID } from './CheckoutFormFields.constants';
import { UseCheckoutFormContext } from './CheckoutFormFields.hooks';

const baseTranslationKey = 'components.modules.checkout.CheckoutFormFields';

type CheckoutFormProps = {
  formId?: string;
  formFields: FormField[];
  context: UseCheckoutFormContext;
  initialFormValues?: Record<string, string>;
  isGuestCheckout?: boolean;
  onUpdateFormField: (formId: string, shouldStoreInSession: boolean, value: Record<string, string>) => void;
  useCardWrapper?: boolean;
};

export type ReactHookFormRegister<T> = {
  register?: UseFormRegister<T>;
};

const CheckoutFormFields = ({
  context,
  formFields,
  initialFormValues,
  isGuestCheckout = false,
  onUpdateFormField,
  useCardWrapper = true,
}: CheckoutFormProps) => {
  const loginContext = useContext(LoginContext);
  const { watch, setValue, formState } = context;
  const dispatch = useAppDispatch();
  const widgetLogin = useWidgetLogin();
  const containment = getContainmentMode();

  const [contactFields, extraFields] = formFields.reduce(
    (acc, field) => {
      if (isStandardFormField(field.id) && field.id !== 'bookingNotes' && field.id !== 'discountCode') {
        acc[0].push(field);
      } else {
        acc[1].push(field);
      }
      return acc;
    },
    [[], [], []],
  );
  const [isMediator, setIsMediator] = useState(guestCheckoutManager().getOptions()?.isMediator ?? false);

  const handleChangeIsMediator = (isMediator: boolean) => {
    if (isMediator) {
      setValue(GUEST_FORM_FIELD_ID, undefined);
      onUpdateFormField('1', false, { [GUEST_FORM_FIELD_ID]: undefined });
    }
    const manager = guestCheckoutManager();
    const options = manager.getOptions();
    manager.setOptions({ ...options, isMediator });
    setIsMediator(isMediator);
  };

  useEffect(() => {
    const subscription = watch((value, e) => {
      onUpdateFormField('1', false, { [e.name]: value[e.name] });
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, onUpdateFormField]);

  useEffect(() => {
    /**
     * if we have formValues already on mount it means we have a saved checkout state
     * and we need to set the values to the form
     */
    Object.entries(initialFormValues ?? []).forEach(([key, value]) => {
      setValue(key, value);
      onUpdateFormField('1', false, { [key]: value });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wrap = (fields: 'extraFields' | 'contactFields' | 'combined', children: React.ReactNode) => {
    let errorsInSection = false;
    /**
     * set error indicator on correct Card
     */
    if (!isEmpty(formState.errors)) {
      const fieldErrorKeys = Object.keys(formState.errors);

      if (fields === 'contactFields') {
        errorsInSection = contactFields.map((field) => field.id).some((key) => fieldErrorKeys.includes(key));
      } else if (fields === 'extraFields') {
        errorsInSection = extraFields.map((field) => field.id).some((key) => fieldErrorKeys.includes(key));
      } else if (fields === 'combined') {
        // Check errors in both contact and extra fields
        const allFieldIds = [...contactFields, ...extraFields].map((field) => field.id);
        errorsInSection = allFieldIds.some((key) => fieldErrorKeys.includes(key));
      }
    }

    return useCardWrapper ? (
      <CardWrapper className={classnames(errorsInSection && 'border-border_negative !border-[1px]')}>
        {children}
      </CardWrapper>
    ) : (
      <>{children}</>
    );
  };

  if (isGuestCheckout) {
    return (
      <>
        {wrap(
          'contactFields',
          <div className="pb-lg">
            <div className="px-lg pt-md">
              <div className="flex flex-col items-center md:flex-row">
                <Header label={_s(`${baseTranslationKey}.guest.title`)} size="md" className="my-md" />
                <PillTabBar
                  className="w-full md:w-auto"
                  items={[
                    {
                      label: _s(`${baseTranslationKey}.guest.tab.guest`),
                      onClick: () => handleChangeIsMediator(false),
                    },
                    {
                      label: _s(`${baseTranslationKey}.guest.tab.client`),
                      onClick: () => handleChangeIsMediator(true),
                    },
                  ]}
                  defaultSelected={isMediator ? 1 : 0}
                />
              </div>
              {!isMediator && (
                <div className="py-2xl">
                  <Alert
                    variant="information"
                    verticalAlign="top"
                    leftSlot={<Icon variant="info-circle" color="fg_info_bold" />}
                    middleSlot={
                      <div className="space-y-xxs flex flex-1 flex-col">
                        <p className="text-m">{_s(`${baseTranslationKey}.guest.alert.body`)}</p>
                        <div className="mt-md flex">
                          <Button
                            className="!px-0"
                            onClick={() => {
                              // Contained, the marketplace's login modal is never
                              // mounted. Framed the login runs in the first-party
                              // popup; a Linked Session has no host to pop out of,
                              // so the widget opens its panel over the form.
                              const email = watch(PROFILE_FORM_FIELD_ID.EMAIL);
                              if (containment === 'embed') {
                                widgetLogin.openLoginPopup({ email });
                                return;
                              }
                              if (widgetLogin.openLoginPanel) {
                                widgetLogin.openLoginPanel({ email });
                                return;
                              }
                              handleLoginClick(dispatch, {
                                ...loginContext,
                                initiatedFrom: 'checkout_booking_summary',
                              });
                            }}
                            variant="link">
                            {_s(`${baseTranslationKey}.guest.alert.action`)}
                          </Button>
                        </div>
                      </div>
                    }
                  />
                </div>
              )}
            </div>
            {isMediator && (
              <Fragment>
                <div className="pl-lg py-md">
                  <Header
                    label={_s(`${baseTranslationKey}.guest.header.guest`)}
                    size="sm"
                    className="text-fg_secondary font-light"
                  />
                </div>
                <CheckoutFormField
                  label="Namn"
                  type="text"
                  values={[]}
                  id={GUEST_FORM_FIELD_ID}
                  context={context}
                  floatStyle
                />
              </Fragment>
            )}
            {contactFields.length > 0 && (
              <Fragment>
                {isMediator && (
                  <div className="pl-lg py-md mt-md">
                    <Header
                      label={_s(`${baseTranslationKey}.guest.header.client`)}
                      size="sm"
                      className="text-fg_secondary font-light"
                    />
                  </div>
                )}
                <div className="px-lg gap-x-md gap-y-lg flex flex-col md:grid md:grid-cols-2">
                  {contactFields.map((field) => {
                    return (
                      <Fragment key={field.id}>
                        <CheckoutFormField
                          {...field}
                          context={context}
                          floatStyle
                          leftPadding={false}
                          rightPadding={false}
                        />
                      </Fragment>
                    );
                  })}
                </div>
              </Fragment>
            )}
          </div>,
        )}

        {extraFields.length > 0 &&
          wrap(
            'extraFields',
            <div className="pb-lg">
              <div className="pl-lg py-md">
                <Header label={_s(`${baseTranslationKey}.header.extra-fields`)} size="lg" />
              </div>
              <div className="space-y-sm flex flex-col">
                {extraFields.map((field) => {
                  return (
                    <Fragment key={field.id}>
                      <CheckoutFormField {...field} context={context} floatStyle />
                    </Fragment>
                  );
                })}
              </div>
            </div>,
          )}
      </>
    );
  }

  // Merge contact and extra fields into single card with single title
  const allFields = [...contactFields, ...extraFields];

  if (allFields.length === 0) {
    return null;
  }

  return wrap(
    'combined',
    <div className="pb-lg">
      <div className="pl-lg py-md">
        <Header label={_s(`${baseTranslationKey}.header.extra-fields`)} size="lg" />
      </div>
      <div className="space-y-sm flex flex-col">
        {allFields.map((field) => (
          <Fragment key={field.id}>
            <CheckoutFormField {...field} context={context} floatStyle />
          </Fragment>
        ))}
      </div>
    </div>,
  );
};

const CheckoutFormField = ({
  id,
  type,
  values,
  label: _label,
  required,
  context,
  floatStyle = false,
  leftPadding = true,
  rightPadding = true,
}: FormField & { context: UseCheckoutFormContext } & {
  floatStyle?: boolean;
  leftPadding?: boolean;
  rightPadding?: boolean;
}) => {
  const {
    register,
    watch,
    formState: { errors },
  } = context;
  const value = watch(id, false);

  const errorMessageToString = typeof errors[id]?.message === 'string' ? (errors[id]?.message as string) : '';

  const label = _label ?? _s(capitalizeFirstLetter(id));
  const textFieldPlaceholder = id === 'bookingNotes' ? _s('BookingNotesPlaceholder') : `${_s('Enter')} ${label}`;
  const requiredFieldLabel = _t('requiredFieldForBooking');

  switch (type) {
    case 'dropdown': {
      return (
        <ListInputSelect
          label={`${label}${required ? '*' : ''}`}
          floatStyle={floatStyle}
          leftPadding={leftPadding}
          error={errorMessageToString}
          value={value}
          {...register(id, {
            ...(required ? { required: requiredFieldLabel } : {}),
            validate: (value) => validateFormFields(id, value),
          })}>
          <Select.Option value="">{_s('Choose')}...</Select.Option>
          {values.map((value, key) => (
            <Select.Option key={key} value={value}>
              {value}
            </Select.Option>
          ))}
        </ListInputSelect>
      );
    }
    case 'text': {
      switch (id) {
        case DEFAULT_FORM_FIELD_ID.BOOKING_NOTES: {
          return (
            <div className="col-span-2">
              <ListInputTextArea
                error={errorMessageToString}
                label={`${label}${required ? '*' : ''}`}
                floatStyle={floatStyle}
                leftPadding={leftPadding}
                rightPadding={rightPadding}
                placeholder={textFieldPlaceholder}
                rows={2}
                {...register(id, {
                  ...(required ? { required: requiredFieldLabel } : {}),
                  validate: (value) => validateFormFields(id, value),
                })}
              />
            </div>
          );
        }
        default: {
          return (
            <ListInput
              label={`${label}${required ? '*' : ''}`}
              floatStyle={floatStyle}
              placeholder={textFieldPlaceholder}
              error={errorMessageToString}
              leftPadding={leftPadding}
              rightPadding={rightPadding}
              id={id}
              {...register(id, {
                ...(required ? { required: requiredFieldLabel } : {}),
                validate: (value) => validateFormFields(id, value),
              })}
            />
          );
        }
      }
    }
    default:
      return null;
  }
};

type IsValid = true;
type IsError = string;

const validateFormFields = (id: FormField['id'], value: string): IsValid | IsError => {
  if (value) {
    switch (id) {
      case 'email':
        return !isEmail(value.trim(), { allow_utf8_local_part: false }) ? _s('invalidEmail') : true;
      case 'mobile':
      case 'phone':
        return !isMobilePhone(value.trim()) || !isLength(value.trim(), { min: 10, max: 14 })
          ? _s('invalidPhone')
          : true;
      case 'nationalId':
        return !isNationalId(value.trim()) ? _s('invalidNationalId') : true;
      case 'postalCode':
        return !isZipcode(value.trim()) ? _s('invalidZipcode') : true;
      default:
        break;
    }
  }
  return true;
};

export default CheckoutFormFields;
